import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { createElement as _createElement } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Box, Grid, Typography, useTheme, TextField, Checkbox, IconButton } from "__barrel_optimize__?names=Box,Checkbox,Grid,IconButton,TextField,Typography,useTheme!=!@mui/material";
import dayjs from "dayjs";
import { useParams } from "next/navigation";
import { useEffect, useState, useRef, useMemo } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { InvoiceApis } from "../../../../../apis/invoice";
import { DeleteIcon2 } from "../../../../../assets/icons/DeleteIcon2";
import { useFetchCustomers, useFetchInvoiceSettings, useFetchProducts, useFetchCompanyDetails } from "../../../../../data/fetch-hooks";
import { changeKeyFormat, underscoreToCamelCase } from "../../../../../utils/helper";
import { useTranslation } from "../../../../i18n/client";
import CollapsibleSection from "../../components/CollapsibleSection";
import ConfirmationModal from "../../components/ConfirmationModal";
import FormField from "../../components/FormField";
import ReusableButton from "../../components/ReusableButton";
import SnackBar from "../../components/Snackbar";
import { formatStringToEuropeanCurrency, taxRateOptions, getUnitOptions } from "../../products/helper";
import { tokens } from "../../theme";
import { InvoiceSchema, constructPayload, invoiceFormDefaultValues, paymentTerms, productDefaultValue, parsePaymentTermsToDays, calculateInvoiceTotals, resolveProductTaxRateValue, normalizeItemsVatForCompanySettings, hasItemsVatChangedForCompanySettings } from "../helper";
import { useQueryClient } from "@tanstack/react-query";
import { shouldShowErrorModal } from "../../../../../lib/errorUtils";
import { customerTypes } from "../../constants/CompanyInformationTypes";
const InvoiceForm = (param)=>{
    let { editData, setDisableSaveAsDraft, setInvoiceValues, invoiceLanguage, setInvoiceLanguage, disableSaveAsDraft, setInvoiceId: setParentInvoiceId, setIsSavingDraft, preselectedCustomerId, onSaveAndPreview } = param;
    var _companyDetails_companyDetails_companyDetailRecord, _companyDetails_companyDetails;
    const [displayFields, setDisplayFields] = useState({
        refNo: false,
        serviceDate: false,
        location: false
    });
    const { lng, invoiceId: routeInvoiceId } = useParams();
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [showSuccess, setShowSuccess] = useState(false);
    const [hasSavedBaseline, setHasSavedBaseline] = useState(false);
    const [hasUserChanges, setHasUserChanges] = useState(false);
    const [invoiceId, setInvoiceId] = useState("");
    const [itemsToDelete, setItemsToDelete] = useState([]);
    // Guard to prevent default-text logic from firing during programmatic form hydration (`reset(...)`).
    // Some `FormField` components can trigger `onSelect` when their value changes programmatically,
    // which would re-apply defaults right after saving a draft.
    const isHydratingRef = useRef(false);
    const preselectionDoneRef = useRef(false);
    const draftInFlightRef = useRef(false);
    const requireServiceDatesRef = useRef(false);
    let { data: products = [], isFetched: productsFetched } = useFetchProducts();
    const { data: customers = [] } = useFetchCustomers();
    const { data: invoiceSettings } = useFetchInvoiceSettings();
    const { data: companyDetails = {} } = useFetchCompanyDetails();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const queryClient = useQueryClient();
    const { t } = useTranslation(lng, "invoice-form");
    const { t: tProducts } = useTranslation(lng, "products");
    // Check if company has VAT exemption (no19 or no4)
    const vatLiability = companyDetails === null || companyDetails === void 0 ? void 0 : (_companyDetails_companyDetails = companyDetails.companyDetails) === null || _companyDetails_companyDetails === void 0 ? void 0 : (_companyDetails_companyDetails_companyDetailRecord = _companyDetails_companyDetails.companyDetailRecord) === null || _companyDetails_companyDetails_companyDetailRecord === void 0 ? void 0 : _companyDetails_companyDetails_companyDetailRecord.vat_liability;
    const hasVatExemption = vatLiability === "no19" || vatLiability === "no4";
    const [messageEnabled, setMessageEnabled] = useState({
        invoiceMessage: true,
        paymentMethod: true,
        discount: false,
        additionalText1: true,
        additionalText2: false,
        message19: true,
        message4: true,
        message13b: true,
        message25a: true
    });
    const [exclusiveBlockModal, setExclusiveBlockModal] = useState({
        open: false,
        targetKey: null,
        targetField: null
    });
    const discountSectionEnabled = useMemo(()=>{
        const invoice = (invoiceSettings === null || invoiceSettings === void 0 ? void 0 : invoiceSettings.invoiceDetailRecord) || {};
        const raw = typeof (invoice === null || invoice === void 0 ? void 0 : invoice.discount_enabled) === "boolean" ? invoice.discount_enabled : typeof (invoice === null || invoice === void 0 ? void 0 : invoice.discountEnabled) === "boolean" ? invoice.discountEnabled : true;
        return Boolean(raw);
    }, [
        invoiceSettings
    ]);
    requireServiceDatesRef.current = displayFields.serviceDate;
    const invoiceSchema = useMemo(()=>InvoiceSchema(t, ()=>requireServiceDatesRef.current), [
        t
    ]);
    const { handleSubmit, formState: { errors, dirtyFields }, reset, control, setValue, watch, trigger, getValues } = useForm({
        resolver: zodResolver(invoiceSchema),
        mode: "onSubmit",
        reValidateMode: "onChange",
        defaultValues: invoiceFormDefaultValues
    });
    const { fields, append, remove, move } = useFieldArray({
        control,
        name: "items"
    });
    const watchFields = watch();
    const markUserChanged = ()=>{
        if (isHydratingRef.current || draftInFlightRef.current) return;
        setHasUserChanges(true);
    };
    const normalizeLanguageCode = (rawLanguage)=>{
        const raw = (rawLanguage || "").toString().toLowerCase().trim();
        if (raw === "german" || raw === "deutsch" || raw === "de") return "de";
        if (raw === "english" || raw === "englisch" || raw === "en") return "en";
        return "de";
    };
    const normalizedInvoiceLanguage = useMemo(()=>{
        return normalizeLanguageCode(invoiceLanguage);
    }, [
        invoiceLanguage
    ]);
    // Track language changes so we don't accidentally overwrite user-entered texts
    // when other dependencies (like items) change.
    const prevInvoiceLanguageRef = useRef(undefined);
    const defaultTexts = useMemo(()=>{
        const invoice = (invoiceSettings === null || invoiceSettings === void 0 ? void 0 : invoiceSettings.invoiceDetailRecord) || {};
        const lang = normalizedInvoiceLanguage;
        const get = (base)=>(invoice === null || invoice === void 0 ? void 0 : invoice["".concat(base, "_").concat(lang)]) || "";
        return {
            invoiceMessage: get("message"),
            paymentMethod: get("payment_method"),
            discount: get("discount"),
            additionalText1: get("additional_text1"),
            additionalText2: get("additional_text2"),
            message19: get("message_19"),
            message4: get("message_4"),
            message13b: get("message_13b"),
            message25a: get("message_25a")
        };
    }, [
        invoiceSettings,
        normalizedInvoiceLanguage
    ]);
    const currentPaymentTermDays = useMemo(()=>{
        return parsePaymentTermsToDays(watchFields.paymentTerms);
    }, [
        watchFields.paymentTerms
    ]);
    const EXCLUSIVE_BLOCK_PAIRS = {
        discount: "paymentMethod",
        paymentMethod: "discount"
    };
    const clearDiscountFields = (shouldDirty)=>{
        setValue("discountDays", "", {
            shouldValidate: true,
            shouldDirty
        });
        setValue("discountPercent", "", {
            shouldValidate: true,
            shouldDirty
        });
        setValue("discount", "", {
            shouldValidate: true,
            shouldDirty
        });
    };
    const applyMessageToggle = (key, fieldName, enabled)=>{
        if (key === "discount" && enabled && currentPaymentTermDays === 0) {
            clearDiscountFields(false);
            return;
        }
        setMessageEnabled((prev)=>({
                ...prev,
                [key]: enabled
            }));
        if (!enabled) {
            setValue(fieldName, "", {
                shouldValidate: true,
                shouldDirty: true
            });
            if (key === "discount") {
                clearDiscountFields(true);
            }
            return;
        }
        const current = (getValues(fieldName) || "").toString();
        const nextDefault = defaultTexts[key] || "";
        if (!current || current.trim() === "") {
            setValue(fieldName, nextDefault, {
                shouldValidate: true,
                shouldDirty: true
            });
        }
    };
    const disableExclusiveBlock = (key)=>{
        const fieldName = key === "discount" ? "discount" : "paymentMethod";
        setMessageEnabled((prev)=>({
                ...prev,
                [key]: false
            }));
        setValue(fieldName, "", {
            shouldValidate: true,
            shouldDirty: true
        });
        if (key === "discount") {
            clearDiscountFields(true);
        }
    };
    const handleExclusiveBlockConfirm = ()=>{
        const { targetKey, targetField } = exclusiveBlockModal;
        if (!targetKey || !targetField) {
            setExclusiveBlockModal({
                open: false,
                targetKey: null,
                targetField: null
            });
            return;
        }
        const otherKey = EXCLUSIVE_BLOCK_PAIRS[targetKey];
        disableExclusiveBlock(otherKey);
        applyMessageToggle(targetKey, targetField, true);
        setExclusiveBlockModal({
            open: false,
            targetKey: null,
            targetField: null
        });
    };
    const handleToggleMessage = (key, fieldName)=>(e)=>{
            var _e_target;
            const enabled = Boolean(e === null || e === void 0 ? void 0 : (_e_target = e.target) === null || _e_target === void 0 ? void 0 : _e_target.checked);
            if (enabled && (key === "discount" || key === "paymentMethod") && messageEnabled[EXCLUSIVE_BLOCK_PAIRS[key]]) {
                setExclusiveBlockModal({
                    open: true,
                    targetKey: key,
                    targetField: fieldName
                });
                return;
            }
            applyMessageToggle(key, fieldName, enabled);
        };
    const renderToggleField = (param)=>{
        let { keyName, fieldName, title, rows, error, style, showDisabledPreview = false } = param;
        const isDiscountUnavailable = keyName === "discount" && currentPaymentTermDays === 0;
        const enabled = messageEnabled[keyName] && !isDiscountUnavailable;
        const previewText = defaultTexts[keyName] || "";
        const placeholder = enabled ? "" : previewText;
        const useDisabledPreview = showDisabledPreview && !enabled && previewText;
        return /*#__PURE__*/ _jsxs(Box, {
            width: "100%",
            children: [
                /*#__PURE__*/ _jsxs(Box, {
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    mb: "15px",
                    children: [
                        /*#__PURE__*/ _jsx(Checkbox, {
                            checked: enabled,
                            onChange: handleToggleMessage(keyName, fieldName),
                            disabled: isDiscountUnavailable,
                            sx: {
                                p: 0.5
                            }
                        }),
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "h6",
                            color: isDiscountUnavailable ? "#B8BACE" : "#515151",
                            children: title
                        })
                    ]
                }),
                keyName === "discount" && /*#__PURE__*/ _jsxs(Grid, {
                    container: true,
                    width: "100%",
                    rowSpacing: "20",
                    columnSpacing: {
                        sm: "20"
                    },
                    mb: "15px",
                    children: [
                        /*#__PURE__*/ _jsx(Grid, {
                            item: true,
                            xs: 12,
                            sm: 6,
                            children: /*#__PURE__*/ _jsx(FormField, {
                                title: t("discountDays"),
                                type: "number",
                                placeholder: "",
                                name: "discountDays",
                                error: errors.discountDays,
                                control: control,
                                inputProps: {
                                    min: "1",
                                    step: "1",
                                    ...currentPaymentTermDays && currentPaymentTermDays > 0 ? {
                                        max: String(currentPaymentTermDays)
                                    } : {}
                                },
                                disabled: !enabled || isDiscountUnavailable,
                                onKeyDown: (e)=>{
                                    // Prevent negative / exponent / decimal input
                                    if ([
                                        "-",
                                        "+",
                                        "e",
                                        "E",
                                        ".",
                                        ","
                                    ].includes(e.key)) e.preventDefault();
                                },
                                onBlur: (e)=>{
                                    var _e_target;
                                    var _e_target_value;
                                    const raw = ((_e_target_value = e === null || e === void 0 ? void 0 : (_e_target = e.target) === null || _e_target === void 0 ? void 0 : _e_target.value) !== null && _e_target_value !== void 0 ? _e_target_value : "").toString().trim();
                                    if (!raw) return;
                                    const n = parseFloat(raw.replace(",", "."));
                                    if (!Number.isFinite(n)) {
                                        setValue("discountDays", "1", {
                                            shouldValidate: true,
                                            shouldDirty: true
                                        });
                                        return;
                                    }
                                    const maxDays = currentPaymentTermDays && currentPaymentTermDays > 0 ? currentPaymentTermDays : null;
                                    const normalized = maxDays !== null ? Math.min(maxDays, Math.max(1, Math.floor(n))) : Math.max(1, Math.floor(n));
                                    if (String(normalized) !== raw) {
                                        setValue("discountDays", String(normalized), {
                                            shouldValidate: true,
                                            shouldDirty: true
                                        });
                                    } else {
                                        trigger("discountDays");
                                    }
                                }
                            })
                        }),
                        /*#__PURE__*/ _jsx(Grid, {
                            item: true,
                            xs: 12,
                            sm: 6,
                            children: /*#__PURE__*/ _jsx(FormField, {
                                title: t("discountPercent"),
                                type: "number",
                                placeholder: "",
                                name: "discountPercent",
                                error: errors.discountPercent,
                                control: control,
                                inputProps: {
                                    min: "0.01",
                                    step: "0.01",
                                    max: "100"
                                },
                                disabled: !enabled || isDiscountUnavailable,
                                onChange: (e)=>{
                                    var _e_target;
                                    var _e_target_value;
                                    // Restrict discount percent to max 100 while typing (not only on blur)
                                    const raw = ((_e_target_value = e === null || e === void 0 ? void 0 : (_e_target = e.target) === null || _e_target === void 0 ? void 0 : _e_target.value) !== null && _e_target_value !== void 0 ? _e_target_value : "").toString().trim();
                                    if (!raw) return;
                                    const n = parseFloat(raw.replace(",", "."));
                                    if (!Number.isFinite(n)) return;
                                    if (n > 100) {
                                        setValue("discountPercent", "100", {
                                            shouldValidate: true,
                                            shouldDirty: true
                                        });
                                    }
                                },
                                onKeyDown: (e)=>{
                                    // Prevent negative / exponent input
                                    if ([
                                        "-",
                                        "+",
                                        "e",
                                        "E"
                                    ].includes(e.key)) e.preventDefault();
                                },
                                onBlur: (e)=>{
                                    var _e_target;
                                    var _e_target_value;
                                    const raw = ((_e_target_value = e === null || e === void 0 ? void 0 : (_e_target = e.target) === null || _e_target === void 0 ? void 0 : _e_target.value) !== null && _e_target_value !== void 0 ? _e_target_value : "").toString().trim();
                                    if (!raw) return;
                                    const n = parseFloat(raw.replace(",", "."));
                                    if (!Number.isFinite(n)) {
                                        setValue("discountPercent", "0.01", {
                                            shouldValidate: true,
                                            shouldDirty: true
                                        });
                                        return;
                                    }
                                    const clamped = Math.min(100, Math.max(0.01, n));
                                    const normalized = clamped.toFixed(2).replace(/\.?0+$/, "");
                                    if (normalized !== raw) {
                                        setValue("discountPercent", normalized, {
                                            shouldValidate: true,
                                            shouldDirty: true
                                        });
                                    } else {
                                        trigger("discountPercent");
                                    }
                                }
                            })
                        })
                    ]
                }),
                useDisabledPreview ? /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(Box, {
                            sx: {
                                display: "none"
                            },
                            children: /*#__PURE__*/ _jsx(FormField, {
                                title: undefined,
                                type: "text",
                                placeholder: "",
                                name: fieldName,
                                multiline: true,
                                rows: rows,
                                error: error,
                                control: control,
                                disabled: true,
                                style: style
                            })
                        }),
                        /*#__PURE__*/ _jsx(TextField, {
                            fullWidth: true,
                            multiline: true,
                            rows: rows,
                            value: previewText,
                            disabled: true,
                            sx: {
                                "& .MuiInputBase-input": {
                                    color: "#B8BACE",
                                    WebkitTextFillColor: "#B8BACE",
                                    padding: "15px 19px",
                                    fontSize: "14px",
                                    fontWeight: 500
                                },
                                "& legend": {
                                    display: "none"
                                },
                                "& fieldset": {
                                    top: 0
                                }
                            }
                        })
                    ]
                }) : /*#__PURE__*/ _jsx(FormField, {
                    title: undefined,
                    type: "text",
                    placeholder: placeholder,
                    name: fieldName,
                    multiline: true,
                    rows: rows,
                    error: error,
                    control: control,
                    disabled: !enabled,
                    style: style
                })
            ]
        });
    };
    const onSubmit = async (values, event)=>{
        var _event_nativeEvent_submitter, _event_nativeEvent;
        if (draftInFlightRef.current) return;
        const { invoiceTotalAmount } = calculateInvoiceTotals(values.items || []);
        if (Number.isFinite(invoiceTotalAmount) && invoiceTotalAmount < 0) return;
        const isSaveAndPreview = (event === null || event === void 0 ? void 0 : (_event_nativeEvent = event.nativeEvent) === null || _event_nativeEvent === void 0 ? void 0 : (_event_nativeEvent_submitter = _event_nativeEvent.submitter) === null || _event_nativeEvent_submitter === void 0 ? void 0 : _event_nativeEvent_submitter.id) === "invoice-save-preview";
        draftInFlightRef.current = true;
        setIsSavingDraft === null || setIsSavingDraft === void 0 ? void 0 : setIsSavingDraft(true);
        try {
            const payload = constructPayload(JSON.parse(JSON.stringify(values)));
            if (invoiceId) payload.invoiceId = invoiceId;
            payload.itemsToDelete = itemsToDelete;
            const { data } = await InvoiceApis.draftInvoice(payload);
            queryClient.setQueryData([
                "invoices",
                data.invoice.invoice_id
            ], data);
            if (routeInvoiceId && routeInvoiceId !== data.invoice.invoice_id) {
                queryClient.setQueryData([
                    "invoices",
                    routeInvoiceId
                ], data);
            }
            queryClient.invalidateQueries({
                queryKey: [
                    "invoices"
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    "products"
                ]
            });
            setInvoiceId(data.invoice.invoice_id);
            setParentInvoiceId && setParentInvoiceId(data.invoice.invoice_id);
            setCurrentFormValues(data.invoice, data.products || products, Boolean(data.products) || productsFetched);
            setShowSuccess(true);
            if (isSaveAndPreview) {
                onSaveAndPreview === null || onSaveAndPreview === void 0 ? void 0 : onSaveAndPreview();
            }
        } catch (err) {
            // Keep save button enabled so user can retry
            setDisableSaveAsDraft(false);
            // Show specific error message
            setErrorMessage(t("saveFailedMessage"));
            if (shouldShowErrorModal(err)) {
                setShowError(true);
            }
        } finally{
            setIsSavingDraft === null || setIsSavingDraft === void 0 ? void 0 : setIsSavingDraft(false);
            draftInFlightRef.current = false;
        }
    };
    const onError = (errors)=>{
        // Validation failed - errors are already displayed via FormField error prop
        // Scroll to first error field if needed
        const firstErrorField = Object.keys(errors)[0];
        if (firstErrorField) {
            const element = document.querySelector('[name="'.concat(firstErrorField, '"]'));
            if (element) {
                element.scrollIntoView({
                    behavior: "smooth",
                    block: "center"
                });
            }
        }
    };
    const setDefaultTexts = (language)=>{
        var _values_lng, _values_lng1, _values_lng2, _values_lng3, _values_lng4, _watchFields_items, _watchFields_items1;
        var _invoiceSettings_invoiceDetailRecord;
        const invoice = (_invoiceSettings_invoiceDetailRecord = invoiceSettings === null || invoiceSettings === void 0 ? void 0 : invoiceSettings.invoiceDetailRecord) !== null && _invoiceSettings_invoiceDetailRecord !== void 0 ? _invoiceSettings_invoiceDetailRecord : {};
        let lng = language || invoice.invoice_language || invoiceLanguage || "de";
        // Convert language to code if needed (e.g., 'German' -> 'de', 'English' -> 'en')
        if (lng && typeof lng === "string") {
            const lowerLng = lng.toLowerCase().trim();
            if (lowerLng === "german" || lowerLng === "deutsch") {
                lng = "de";
            } else if (lowerLng === "english" || lowerLng === "englisch") {
                lng = "en";
            }
        }
        // Fallback to 'de' if lng is not 'en' or 'de'
        if (lng !== "en" && lng !== "de") {
            lng = "de";
        }
        const values = {
            en: {},
            de: {}
        };
        Object.entries(invoice).forEach((item)=>{
            if (item[0].includes("_en")) {
                const key = item[0].replace("_en", "");
                values.en[key] = item[1];
            } else if (item[0].includes("_de")) {
                const key = item[0].replace("_de", "");
                values.de[key] = item[1];
            }
        });
        // Apply defaults when:
        // - field is enabled (checkbox)
        // - user hasn't edited the field (not dirty)
        // This ensures changing the invoice language updates defaults,
        // while still preserving user-entered custom texts.
        const isFieldDirty = (fieldName)=>Boolean(dirtyFields === null || dirtyFields === void 0 ? void 0 : dirtyFields[fieldName]);
        const applyDefault = (fieldName, enabled, value)=>{
            if (!enabled) return;
            if (isFieldDirty(fieldName)) return;
            setValue(fieldName, value || "", {
                shouldDirty: false,
                shouldTouch: false,
                shouldValidate: true
            });
        };
        applyDefault("invoiceMessage", messageEnabled.invoiceMessage, (_values_lng = values[lng]) === null || _values_lng === void 0 ? void 0 : _values_lng.message);
        applyDefault("paymentMethod", messageEnabled.paymentMethod, (_values_lng1 = values[lng]) === null || _values_lng1 === void 0 ? void 0 : _values_lng1.payment_method);
        applyDefault("discount", messageEnabled.discount, (_values_lng2 = values[lng]) === null || _values_lng2 === void 0 ? void 0 : _values_lng2.discount);
        applyDefault("additionalText1", messageEnabled.additionalText1, (_values_lng3 = values[lng]) === null || _values_lng3 === void 0 ? void 0 : _values_lng3.additional_text1);
        applyDefault("additionalText2", messageEnabled.additionalText2, (_values_lng4 = values[lng]) === null || _values_lng4 === void 0 ? void 0 : _values_lng4.additional_text2);
        // Set message fields based on conditions, only if empty
        if (vatLiability === "no19") {
            var _values_lng5;
            applyDefault("message19", messageEnabled.message19, (_values_lng5 = values[lng]) === null || _values_lng5 === void 0 ? void 0 : _values_lng5.message_19);
        }
        if (vatLiability === "no4") {
            var _values_lng6;
            applyDefault("message4", messageEnabled.message4, (_values_lng6 = values[lng]) === null || _values_lng6 === void 0 ? void 0 : _values_lng6.message_4);
        }
        // Check if any item has vatType 13b or 25a
        const has13b = (_watchFields_items = watchFields.items) === null || _watchFields_items === void 0 ? void 0 : _watchFields_items.some((item)=>{
            if (!item.vat) return false;
            const vatStr = item.vat.toString();
            const vatType = item.vatType;
            return vatType === "13b" || vatStr.includes("_13b") || vatStr === "0_13b";
        });
        const has25a = (_watchFields_items1 = watchFields.items) === null || _watchFields_items1 === void 0 ? void 0 : _watchFields_items1.some((item)=>{
            if (!item.vat) return false;
            const vatStr = item.vat.toString();
            const vatType = item.vatType;
            return vatType === "25a" || vatStr.includes("_25a") || vatStr === "0_25a";
        });
        if (has13b) {
            var _values_lng7;
            applyDefault("message13b", messageEnabled.message13b, (_values_lng7 = values[lng]) === null || _values_lng7 === void 0 ? void 0 : _values_lng7.message_13b);
        }
        if (has25a) {
            var _values_lng8;
            applyDefault("message25a", messageEnabled.message25a, (_values_lng8 = values[lng]) === null || _values_lng8 === void 0 ? void 0 : _values_lng8.message_25a);
        }
    };
    // Helper functions to check if message fields should be shown
    const shouldShowMessage19 = ()=>{
        return vatLiability === "no19";
    };
    const shouldShowMessage4 = ()=>{
        return vatLiability === "no4";
    };
    const shouldShowMessage13b = ()=>{
        var _watchFields_items;
        return (_watchFields_items = watchFields.items) === null || _watchFields_items === void 0 ? void 0 : _watchFields_items.some((item)=>{
            if (!item.vat) return false;
            const vatStr = item.vat.toString();
            const vatType = item.vatType;
            // Check if vatType is 13b or if vat contains _13b
            return vatType === "13b" || vatStr.includes("_13b") || vatStr === "0_13b";
        });
    };
    const shouldShowMessage25a = ()=>{
        var _watchFields_items;
        return (_watchFields_items = watchFields.items) === null || _watchFields_items === void 0 ? void 0 : _watchFields_items.some((item)=>{
            if (!item.vat) return false;
            const vatStr = item.vat.toString();
            const vatType = item.vatType;
            // Check if vatType is 25a or if vat contains _25a
            return vatType === "25a" || vatStr.includes("_25a") || vatStr === "0_25a";
        });
    };
    const setCurrentFormValues = function(editData) {
        let productSource = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : products, productSourceFetched = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : productsFetched;
        // Ensure we can always build a valid product object for the autocomplete,
        // even if the product list is stale/not yet loaded.
        const ensureProductOptionShape = (p)=>{
            if (!p || typeof p !== "object") return p;
            const next = {
                ...p
            };
            if (!next.label) next.label = next.product_name || next.productName || "";
            if (!next.value) next.value = next.product_id || next.productId || next.value;
            return next;
        };
        const buildFallbackProductOption = (productId, productName)=>{
            if (!productId && !productName) return null;
            const id = productId !== undefined && productId !== null ? String(productId) : null;
            const name = (productName || "").toString();
            return {
                product_id: id,
                product_name: name,
                label: name || id || "",
                value: id
            };
        };
        const invoice = changeKeyFormat(editData, underscoreToCamelCase);
        invoice.customerId = customerList.find((customer)=>customer.customer_id === invoice.customerId);
        const items = invoice.items && invoice.items.map((item)=>{
            const obj = changeKeyFormat(item, underscoreToCamelCase);
            // Combine tax_rate and vat_type for display
            obj.vat = obj.vatType ? "".concat(obj.vat, "_").concat(obj.vatType) : String(obj.vat).replace(".00", "");
            obj.unitPrice = formatStringToEuropeanCurrency(obj.unitPrice);
            obj.description = obj.description || "";
            obj.quantity = obj.quantity.toString();
            // Keep a stable product selection shape for the autocomplete + zod schema.
            // Backend usually returns item.productId as an id; we resolve it to the full product object if possible.
            const rawProductId = obj.productId;
            const resolved = (productSource || []).find((product)=>String(product.product_id) === String(rawProductId));
            const missingLiveProduct = productSourceFetched && rawProductId && !resolved;
            obj.productId = resolved ? ensureProductOptionShape(resolved) : buildFallbackProductOption(missingLiveProduct ? null : rawProductId, obj.productName);
            obj.lineType = obj.lineType || (obj.productType === "one-time" ? "one_time" : "catalog");
            if (obj.lineType === "one_time") {
                obj.productType = "one-time";
            }
            if (missingLiveProduct && obj.productName && String(obj.productName).trim() !== "") {
                obj.productType = "one-time";
                obj.lineType = "one_time";
            }
            // Also ensure we keep productName for validation/PDF rendering
            if (!obj.productName || String(obj.productName).trim() === "") {
                var _obj_productId, _obj_productId1;
                obj.productName = ((_obj_productId = obj.productId) === null || _obj_productId === void 0 ? void 0 : _obj_productId.product_name) || ((_obj_productId1 = obj.productId) === null || _obj_productId1 === void 0 ? void 0 : _obj_productId1.label) || obj.productName || "";
            }
            return obj;
        });
        invoice.vat = invoice.vat ? "".concat(parseFloat(invoice.vat)) : "0";
        const rawItems = items && items.length > 0 ? items : [
            productDefaultValue
        ];
        const normalizedItems = normalizeItemsVatForCompanySettings(rawItems, hasVatExemption, productSource || products);
        const vatAdjustedByCompanySettings = hasItemsVatChangedForCompanySettings(rawItems, normalizedItems);
        invoice.items = normalizedItems;
        invoice.referenceNumber = invoice.referenceNumber || "";
        // Ensure text fields have default values
        invoice.invoiceMessage = invoice.invoiceMessage || "";
        invoice.paymentMethod = invoice.paymentMethod || "";
        invoice.discount = invoice.discount || "";
        // Discount fields: backend may return numeric derived fields only (e.g. `discountDaysNumber` / `discountPercentNumber`).
        // Prefer explicit string fields if present; otherwise hydrate from numeric fields; finally fall back to defaults.
        const hasNonEmpty = (v)=>v !== undefined && v !== null && String(v).trim() !== "";
        // Days
        if (!hasNonEmpty(invoice.discountDays)) {
            const rawDaysNumber = invoice.discountDaysNumber;
            const n = Number(rawDaysNumber);
            if (Number.isFinite(n)) {
                invoice.discountDays = String(Math.max(1, Math.floor(n)));
            } else {
                invoice.discountDays = "7";
            }
        } else {
            invoice.discountDays = String(invoice.discountDays);
        }
        // Percent
        if (!hasNonEmpty(invoice.discountPercent)) {
            const rawPercentNumber = invoice.discountPercentNumber;
            const n = Number(rawPercentNumber);
            if (Number.isFinite(n)) {
                const clamped = Math.min(100, Math.max(0.01, n));
                invoice.discountPercent = clamped.toFixed(2).replace(/\.?0+$/, "");
            } else {
                invoice.discountPercent = "2";
            }
        } else {
            invoice.discountPercent = String(invoice.discountPercent);
        }
        invoice.additionalText1 = invoice.additionalText1 || "";
        invoice.additionalText2 = invoice.additionalText2 || "";
        invoice.message19 = invoice.message19 || "";
        invoice.message4 = invoice.message4 || "";
        invoice.message13b = invoice.message13b || "";
        invoice.message25a = invoice.message25a || "";
        // Ensure special messages are enabled by default when applicable (and prefilled so they show in PDF)
        try {
            var _invoice_items, _invoice_items1;
            const rawLang = (invoice.invoiceLanguage || invoiceLanguage || "de").toString().toLowerCase().trim();
            const lang = rawLang === "en" || rawLang === "english" || rawLang === "englisch" ? "en" : "de";
            const invoiceDefaults = (invoiceSettings === null || invoiceSettings === void 0 ? void 0 : invoiceSettings.invoiceDetailRecord) || {};
            const getDefault = (base)=>(invoiceDefaults === null || invoiceDefaults === void 0 ? void 0 : invoiceDefaults["".concat(base, "_").concat(lang)]) || "";
            const has13bFromInvoice = (_invoice_items = invoice.items) === null || _invoice_items === void 0 ? void 0 : _invoice_items.some((it)=>{
                var _it_vat;
                const vatStr = ((_it_vat = it === null || it === void 0 ? void 0 : it.vat) !== null && _it_vat !== void 0 ? _it_vat : "").toString();
                var _it_vatType;
                const vatType = (_it_vatType = it === null || it === void 0 ? void 0 : it.vatType) !== null && _it_vatType !== void 0 ? _it_vatType : it === null || it === void 0 ? void 0 : it.vat_type;
                return vatType === "13b" || vatStr.includes("_13b") || vatStr === "0_13b";
            });
            const has25aFromInvoice = (_invoice_items1 = invoice.items) === null || _invoice_items1 === void 0 ? void 0 : _invoice_items1.some((it)=>{
                var _it_vat;
                const vatStr = ((_it_vat = it === null || it === void 0 ? void 0 : it.vat) !== null && _it_vat !== void 0 ? _it_vat : "").toString();
                var _it_vatType;
                const vatType = (_it_vatType = it === null || it === void 0 ? void 0 : it.vatType) !== null && _it_vatType !== void 0 ? _it_vatType : it === null || it === void 0 ? void 0 : it.vat_type;
                return vatType === "25a" || vatStr.includes("_25a") || vatStr === "0_25a";
            });
            if (vatLiability === "no4" && (!invoice.message4 || invoice.message4.trim() === "")) {
                invoice.message4 = getDefault("message_4") || "";
            }
            if (has13bFromInvoice && (!invoice.message13b || invoice.message13b.trim() === "")) {
                invoice.message13b = getDefault("message_13b") || "";
            }
            if (has25aFromInvoice && (!invoice.message25a || invoice.message25a.trim() === "")) {
                invoice.message25a = getDefault("message_25a") || "";
            }
        } catch (e) {
        // ignore default-fill errors
        }
        setMessageEnabled({
            invoiceMessage: Boolean((invoice.invoiceMessage || "").toString().trim()),
            paymentMethod: (()=>{
                const paymentMethodEnabled = Boolean((invoice.paymentMethod || "").toString().trim());
                const discountEnabled = Boolean((invoice.discount || "").toString().trim());
                if (paymentMethodEnabled && discountEnabled) {
                    invoice.discount = "";
                    invoice.discountDays = "";
                    invoice.discountPercent = "";
                    return true;
                }
                return paymentMethodEnabled;
            })(),
            discount: (()=>{
                const paymentMethodEnabled = Boolean((invoice.paymentMethod || "").toString().trim());
                const discountEnabled = Boolean((invoice.discount || "").toString().trim());
                if (paymentMethodEnabled && discountEnabled) return false;
                return discountEnabled;
            })(),
            additionalText1: Boolean((invoice.additionalText1 || "").toString().trim()),
            additionalText2: Boolean((invoice.additionalText2 || "").toString().trim()),
            message19: Boolean((invoice.message19 || "").toString().trim()),
            // Keep these active by default
            message4: true,
            message13b: true,
            message25a: true
        });
        // Always set documentType to 'invoice' (field is disabled)
        invoice.documentType = "invoice";
        // Ensure invoiceLanguage is always 'en' | 'de' (backend/settings may send 'German'/'English')
        invoice.invoiceLanguage = normalizeLanguageCode(invoice.invoiceLanguage || invoiceLanguage);
        productList = (productSource || []).map((product)=>{
            product.label = product.product_name;
            product.value = product.product_id;
            return product;
        });
        setInvoiceLanguage(invoice.invoiceLanguage);
        invoice.invoiceDate = dayjs(invoice.invoiceDate);
        if (invoice.serviceStartDate) invoice.serviceStartDate = dayjs(invoice.serviceStartDate) || dayjs();
        if (invoice.serviceEndDate) invoice.serviceEndDate = dayjs(invoice.serviceEndDate) || dayjs();
        // Programmatic hydration: block any default-text application that might be triggered by controlled selects.
        isHydratingRef.current = true;
        reset(invoice, {
            keepDirty: false,
            keepDirtyValues: false,
            keepDefaultValues: false
        });
        setInvoiceValues(invoice);
        setHasSavedBaseline(true);
        setHasUserChanges(vatAdjustedByCompanySettings);
        setDisableSaveAsDraft(!vatAdjustedByCompanySettings);
        // Release guard on next tick (after controlled inputs finish syncing).
        setTimeout(()=>{
            isHydratingRef.current = false;
            const hydratedItems = getValues("items") || [];
            const syncedItems = normalizeItemsVatForCompanySettings(hydratedItems, hasVatExemption, productSource || products);
            let additionalVatChange = false;
            syncedItems.forEach((item, index)=>{
                var _getValues;
                const currentVat = ((_getValues = getValues("items.".concat(index, ".vat"))) !== null && _getValues !== void 0 ? _getValues : "").toString();
                var _getValues1;
                const currentVatType = (_getValues1 = getValues("items.".concat(index, ".vatType"))) !== null && _getValues1 !== void 0 ? _getValues1 : null;
                var _item_vatType;
                if (currentVat !== item.vat || currentVatType !== ((_item_vatType = item.vatType) !== null && _item_vatType !== void 0 ? _item_vatType : null)) {
                    additionalVatChange = true;
                    setValue("items[".concat(index, "].vat"), item.vat, {
                        shouldValidate: true,
                        shouldDirty: true
                    });
                    var _item_vatType1;
                    setValue("items[".concat(index, "].vatType"), (_item_vatType1 = item.vatType) !== null && _item_vatType1 !== void 0 ? _item_vatType1 : null, {
                        shouldValidate: true,
                        shouldDirty: true
                    });
                    trigger("items[".concat(index, "].vat"));
                }
            });
            if (additionalVatChange || vatAdjustedByCompanySettings) {
                setHasUserChanges(true);
                setDisableSaveAsDraft(false);
            }
        }, 0);
    };
    const handleItemDelete = (index, item)=>{
        var _item_itemId;
        const itemId = (_item_itemId = item === null || item === void 0 ? void 0 : item.itemId) !== null && _item_itemId !== void 0 ? _item_itemId : getValues("items.".concat(index, ".itemId"));
        if (itemId) {
            setItemsToDelete((curr)=>curr.includes(itemId) ? curr : [
                    ...curr,
                    itemId
                ]);
        }
        if (fields.length <= 1) {
            // Keep one empty row in the UI, but treat clearing the last item as a real change.
            remove(index);
            append(productDefaultValue);
        } else {
            remove(index);
        }
        markUserChanged();
        trigger("items");
    };
    const handleItemAppend = ()=>{
        append(productDefaultValue);
        markUserChanged();
        trigger("items");
    };
    const handleItemMoveUp = (index)=>{
        if (index <= 0) return;
        move(index, index - 1);
        markUserChanged();
    };
    const handleItemMoveDown = (index)=>{
        if (index >= fields.length - 1) return;
        move(index, index + 1);
        markUserChanged();
    };
    useEffect(()=>{
        if (invoiceSettings === null || invoiceSettings === void 0 ? void 0 : invoiceSettings.invoiceDetailRecord) {
            const invoice = invoiceSettings.invoiceDetailRecord;
            const hasCustomerSelected = Boolean(watchFields.customerId && Object.keys(watchFields.customerId).length > 0);
            const currentFormLang = (watchFields.invoiceLanguage || "").toString().trim();
            // Only set invoice language from settings if:
            // - not editing an existing invoice
            // - no customer is selected (customer overrides settings)
            // - the user hasn't already picked a language in the form
            if (invoice.invoice_language && !editData && !hasCustomerSelected && !currentFormLang) {
                const code = normalizeLanguageCode(invoice.invoice_language);
                setValue("invoiceLanguage", code);
                setInvoiceLanguage(code);
            }
            // Always set documentType to 'invoice' (field is disabled)
            setValue("documentType", "invoice");
            // Set default VAT percentage from invoice settings
            if (invoice.vat_percentage !== undefined && invoice.vat_percentage !== null) {
                setValue("vat", invoice.vat_percentage.toString());
            }
            setDisplayFields({
                refNo: invoice.reference_number,
                serviceDate: invoice.service_date,
                location: invoice.location
            });
            // Global invoice-settings toggle: if discount is disabled, force-hide and clear discount fields.
            if (!discountSectionEnabled) {
                setMessageEnabled((prev)=>({
                        ...prev,
                        discount: false
                    }));
                setValue("discountDays", "", {
                    shouldValidate: true,
                    shouldDirty: false
                });
                setValue("discountPercent", "", {
                    shouldValidate: true,
                    shouldDirty: false
                });
                setValue("discount", "", {
                    shouldValidate: true,
                    shouldDirty: false
                });
            }
            // Only set default texts if not editing existing invoice
            // Also skip once a draft has been created (invoiceId exists) or while we're hydrating after save/reset.
            if (!editData && !invoiceId && !isHydratingRef.current) {
                // Prefer current form language; otherwise fall back to settings language
                const langToApply = normalizeLanguageCode(currentFormLang || invoice.invoice_language);
                setDefaultTexts(langToApply);
            }
        }
    }, [
        invoiceSettings,
        editData,
        watchFields.customerId,
        watchFields.invoiceLanguage,
        invoiceId,
        discountSectionEnabled,
        setValue
    ]);
    useEffect(()=>{
        const isDiscountInactive = !messageEnabled.discount || currentPaymentTermDays === 0;
        if (currentPaymentTermDays === 0) {
            setMessageEnabled((prev)=>prev.discount ? {
                    ...prev,
                    discount: false
                } : prev);
        }
        if (isDiscountInactive) {
            const hasDiscountData = [
                "discount",
                "discountDays",
                "discountPercent"
            ].some((field)=>(getValues(field) || "").toString().trim() !== "");
            if (hasDiscountData) {
                clearDiscountFields(false);
            }
            return;
        }
        if (currentPaymentTermDays === null) return;
        const rawDiscountDays = (getValues("discountDays") || "").toString().trim();
        const discountDays = parseFloat(rawDiscountDays.replace(",", "."));
        if (Number.isFinite(discountDays) && discountDays > currentPaymentTermDays) {
            setValue("discountDays", String(currentPaymentTermDays), {
                shouldValidate: true,
                shouldDirty: false
            });
        } else {
            trigger("discountDays");
        }
    }, [
        currentPaymentTermDays,
        messageEnabled.discount,
        getValues,
        setValue,
        trigger
    ]);
    useEffect(()=>{
        if (editData && customers.length > 0 && invoiceSettings) {
            setCurrentFormValues(editData);
        }
    }, [
        editData,
        customers,
        products,
        invoiceSettings,
        vatLiability
    ]);
    useEffect(()=>{
        const { invoiceTotalAmount } = calculateInvoiceTotals(watchFields.items || []);
        const hasNegativeTotal = Number.isFinite(invoiceTotalAmount) && invoiceTotalAmount < 0;
        if (hasNegativeTotal) {
            setDisableSaveAsDraft(true);
            return;
        }
        // Never-saved invoice: always allow Save draft / Save and Preview.
        // Do not gate on hasUserChanges — product autocomplete and settings hydration
        // mark dirty inconsistently across environments (local vs production).
        if (!hasSavedBaseline) {
            setDisableSaveAsDraft(false);
            return;
        }
        setDisableSaveAsDraft(!hasUserChanges);
    }, [
        hasSavedBaseline,
        hasUserChanges,
        watchFields.items,
        setDisableSaveAsDraft
    ]);
    useEffect(()=>{
        const subscription = watch(()=>{
            // Get the latest form values to ensure productName is included
            const currentValues = getValues();
            // Ensure productName is always set from productId for custom products
            const updatedValues = {
                ...currentValues
            };
            if (updatedValues.items) {
                updatedValues.items = updatedValues.items.map((item)=>{
                    const productId = item.productId;
                    // If productId has product_name but productName is missing, set it
                    if (productId && typeof productId === "object" && productId.product_name && !item.productName) {
                        return {
                            ...item,
                            productName: productId.product_name
                        };
                    }
                    // If productId has label but productName is missing, set it
                    if (productId && typeof productId === "object" && productId.label && !item.productName && !productId.product_id) {
                        return {
                            ...item,
                            productName: productId.label
                        };
                    }
                    return item;
                });
            }
            const totals = calculateInvoiceTotals(updatedValues.items || []);
            updatedValues.invoiceAmountExclVat = totals.invoiceAmountExclVat;
            updatedValues.invoiceTotalAmount = totals.invoiceTotalAmount;
            // Create a new object reference to ensure React detects the change
            setInvoiceValues(updatedValues);
        });
        return ()=>subscription.unsubscribe();
    }, [
        watch,
        getValues,
        setInvoiceValues
    ]);
    // Sync item tax rates when company VAT liability changes (or draft items are out of sync)
    useEffect(()=>{
        const items = watchFields.items;
        if (!(items === null || items === void 0 ? void 0 : items.length) || isHydratingRef.current) return;
        const syncedItems = normalizeItemsVatForCompanySettings(items, hasVatExemption, products);
        let vatChanged = false;
        syncedItems.forEach((item, index)=>{
            var _items_index, _items_index1;
            var _items_index_vat;
            const vatStr = ((_items_index_vat = (_items_index = items[index]) === null || _items_index === void 0 ? void 0 : _items_index.vat) !== null && _items_index_vat !== void 0 ? _items_index_vat : "").toString();
            var _items_index_vatType;
            const currentVatType = (_items_index_vatType = (_items_index1 = items[index]) === null || _items_index1 === void 0 ? void 0 : _items_index1.vatType) !== null && _items_index_vatType !== void 0 ? _items_index_vatType : null;
            var _item_vatType;
            if (vatStr === item.vat && currentVatType === ((_item_vatType = item.vatType) !== null && _item_vatType !== void 0 ? _item_vatType : null)) return;
            vatChanged = true;
            setValue("items[".concat(index, "].vat"), item.vat, {
                shouldValidate: true,
                shouldDirty: true
            });
            var _item_vatType1;
            setValue("items[".concat(index, "].vatType"), (_item_vatType1 = item.vatType) !== null && _item_vatType1 !== void 0 ? _item_vatType1 : null, {
                shouldValidate: true,
                shouldDirty: true
            });
            trigger("items[".concat(index, "].vat"));
        });
        if (vatChanged) {
            setHasUserChanges(true);
            setDisableSaveAsDraft(false);
        }
    }, [
        hasVatExemption,
        watchFields.items,
        products,
        setValue,
        trigger,
        setDisableSaveAsDraft
    ]);
    // Sync productName with productId when typing custom products
    useEffect(()=>{
        var _watchFields_items;
        (_watchFields_items = watchFields.items) === null || _watchFields_items === void 0 ? void 0 : _watchFields_items.forEach((item, index)=>{
            const productId = item.productId;
            // If productId is a custom typed object (has label/product_name but no product_id), sync productName
            if (productId && typeof productId === "object" && productId.label && !productId.product_id) {
                const typedName = productId.product_name || productId.label;
                if (typedName && item.productName !== typedName) {
                    setValue("items[".concat(index, "].productName"), typedName);
                }
            }
        });
    }, [
        watchFields.items,
        setValue
    ]);
    // Update message fields when items or invoice language change (to show/hide §13b and §25a fields)
    useEffect(()=>{
        if ((invoiceSettings === null || invoiceSettings === void 0 ? void 0 : invoiceSettings.invoiceDetailRecord) && invoiceLanguage) {
            var _watchFields_items, _watchFields_items1;
            const invoice = invoiceSettings.invoiceDetailRecord;
            const values = {
                en: {},
                de: {}
            };
            Object.entries(invoice).forEach((item)=>{
                if (item[0].includes("_en")) {
                    const key = item[0].replace("_en", "");
                    values.en[key] = item[1];
                } else if (item[0].includes("_de")) {
                    const key = item[0].replace("_de", "");
                    values.de[key] = item[1];
                }
            });
            // Only apply default additional texts when the LANGUAGE actually changes,
            // and only if the user hasn't typed anything (prevents clearing after save/reset).
            const prevLang = prevInvoiceLanguageRef.current;
            const langChanged = prevLang !== invoiceLanguage;
            if (langChanged && !editData) {
                const currentAdditional1 = (getValues("additionalText1") || "").toString();
                const currentAdditional2 = (getValues("additionalText2") || "").toString();
                if (messageEnabled.additionalText1 && currentAdditional1.trim() === "") {
                    var _values_invoiceLanguage;
                    setValue("additionalText1", ((_values_invoiceLanguage = values[invoiceLanguage]) === null || _values_invoiceLanguage === void 0 ? void 0 : _values_invoiceLanguage.additional_text1) || "", {
                        shouldDirty: false,
                        shouldValidate: true
                    });
                }
                if (messageEnabled.additionalText2 && currentAdditional2.trim() === "") {
                    var _values_invoiceLanguage1;
                    setValue("additionalText2", ((_values_invoiceLanguage1 = values[invoiceLanguage]) === null || _values_invoiceLanguage1 === void 0 ? void 0 : _values_invoiceLanguage1.additional_text2) || "", {
                        shouldDirty: false,
                        shouldValidate: true
                    });
                }
            }
            prevInvoiceLanguageRef.current = invoiceLanguage;
            // Update message19 and message4 based on vatLiability when language changes
            // Only update if field is empty to preserve user edits
            if (vatLiability === "no19") {
                const currentValue = watchFields.message19;
                if (messageEnabled.message19 && (!currentValue || currentValue.trim() === "")) {
                    var _values_invoiceLanguage2;
                    setValue("message19", ((_values_invoiceLanguage2 = values[invoiceLanguage]) === null || _values_invoiceLanguage2 === void 0 ? void 0 : _values_invoiceLanguage2.message_19) || "", {
                        shouldDirty: false,
                        shouldValidate: true
                    });
                }
            }
            if (vatLiability === "no4") {
                const currentValue = watchFields.message4;
                if (messageEnabled.message4 && (!currentValue || currentValue.trim() === "")) {
                    var _values_invoiceLanguage3;
                    setValue("message4", ((_values_invoiceLanguage3 = values[invoiceLanguage]) === null || _values_invoiceLanguage3 === void 0 ? void 0 : _values_invoiceLanguage3.message_4) || "", {
                        shouldDirty: false,
                        shouldValidate: true
                    });
                }
            }
            // Check and set message13b and message25a based on items
            const has13b = (_watchFields_items = watchFields.items) === null || _watchFields_items === void 0 ? void 0 : _watchFields_items.some((item)=>{
                if (!item.vat) return false;
                const vatStr = item.vat.toString();
                const vatType = item.vatType;
                return vatType === "13b" || vatStr.includes("_13b") || vatStr === "0_13b";
            });
            const has25a = (_watchFields_items1 = watchFields.items) === null || _watchFields_items1 === void 0 ? void 0 : _watchFields_items1.some((item)=>{
                if (!item.vat) return false;
                const vatStr = item.vat.toString();
                const vatType = item.vatType;
                return vatType === "25a" || vatStr.includes("_25a") || vatStr === "0_25a";
            });
            if (has13b) {
                // Set default text from invoice settings if field is empty or undefined
                const currentValue = watchFields.message13b;
                if (messageEnabled.message13b && (!currentValue || currentValue.trim() === "")) {
                    var _values_invoiceLanguage4;
                    setValue("message13b", ((_values_invoiceLanguage4 = values[invoiceLanguage]) === null || _values_invoiceLanguage4 === void 0 ? void 0 : _values_invoiceLanguage4.message_13b) || "", {
                        shouldDirty: false,
                        shouldValidate: true
                    });
                }
            } else {
            // Don't clear message13b automatically; user may have disabled it and should still be able
            // to re-enable it when coming back to edit.
            }
            if (has25a) {
                // Set default text from invoice settings if field is empty or undefined
                const currentValue = watchFields.message25a;
                if (messageEnabled.message25a && (!currentValue || currentValue.trim() === "")) {
                    var _values_invoiceLanguage5;
                    setValue("message25a", ((_values_invoiceLanguage5 = values[invoiceLanguage]) === null || _values_invoiceLanguage5 === void 0 ? void 0 : _values_invoiceLanguage5.message_25a) || "", {
                        shouldDirty: false,
                        shouldValidate: true
                    });
                }
            } else {
            // Don't clear message25a automatically; user may have disabled it and should still be able
            // to re-enable it when coming back to edit.
            }
        }
    }, [
        watchFields.items,
        invoiceSettings,
        invoiceLanguage,
        vatLiability,
        setValue,
        watchFields.message13b,
        watchFields.message25a,
        editData,
        messageEnabled.additionalText1,
        messageEnabled.additionalText2,
        messageEnabled.message19,
        messageEnabled.message4,
        messageEnabled.message13b,
        messageEnabled.message25a
    ]);
    let productList = products.filter((product)=>product.product_type != "-").map((product)=>{
        product.label = product.product_name;
        product.value = product.product_id;
        return product;
    });
    useEffect(()=>{
        if (products) {
            productList = products.map((product)=>{
                product.label = product.product_name;
                product.value = product.product_id;
                return product;
            });
        }
    });
    const customerList = customers.filter((customer)=>customer.customer_status.toLowerCase() === "active").map((customer)=>{
        // Use company_name for business customers, otherwise use first_name + last_name
        customer.label = customer.customer_type === customerTypes.businessCustomer && customer.company_name ? customer.company_name : "".concat(customer.first_name, " ").concat(customer.last_name);
        customer.value = customer.customer_id;
        return customer;
    });
    // Reset preselection ref when preselectedCustomerId changes
    useEffect(()=>{
        preselectionDoneRef.current = false;
    }, [
        preselectedCustomerId
    ]);
    // Preselect customer when preselectedCustomerId is provided and customers are loaded
    useEffect(()=>{
        if (!preselectedCustomerId || customers.length === 0 || editData || preselectionDoneRef.current) {
            return;
        }
        // Convert preselectedCustomerId to number for comparison
        const customerIdNum = typeof preselectedCustomerId === "string" ? parseInt(preselectedCustomerId, 10) : preselectedCustomerId;
        if (!customerIdNum || isNaN(customerIdNum)) {
            return;
        }
        // Check if customer is already selected to avoid re-setting
        const currentValue = watchFields.customerId;
        const currentCustomerId = (currentValue === null || currentValue === void 0 ? void 0 : currentValue.customer_id) || (currentValue === null || currentValue === void 0 ? void 0 : currentValue.value);
        if (currentCustomerId === customerIdNum || currentCustomerId === preselectedCustomerId) {
            preselectionDoneRef.current = true;
            return; // Already selected, no need to set again
        }
        // Build customerList to find the customer (same logic as the customerList variable)
        const activeCustomers = customers.filter((customer)=>{
            const status = customer.customer_status || customer.status;
            return status && String(status).toLowerCase() === "active";
        });
        // Try to find customer by comparing both number and string (in case of type mismatch)
        const preselectedCustomer = activeCustomers.find((c)=>c.customer_id === customerIdNum || c.customer_id === preselectedCustomerId || String(c.customer_id) === String(preselectedCustomerId));
        if (!preselectedCustomer) {
            return;
        }
        // Build customer object in the exact format expected by customerList
        // Use company_name for business customers, otherwise use first_name + last_name
        const customerLabel = preselectedCustomer.customer_type === customerTypes.businessCustomer && preselectedCustomer.company_name ? preselectedCustomer.company_name : "".concat(preselectedCustomer.first_name, " ").concat(preselectedCustomer.last_name);
        const customerOption = {
            ...preselectedCustomer,
            label: customerLabel,
            value: preselectedCustomer.customer_id
        };
        // Use setTimeout to ensure form is ready
        const timeoutId = setTimeout(()=>{
            setValue("customerId", customerOption, {
                shouldValidate: true,
                shouldDirty: false
            });
            preselectionDoneRef.current = true;
            // Set payment terms from customer
            if (preselectedCustomer.payment_terms) {
                setValue("paymentTerms", preselectedCustomer.payment_terms, {
                    shouldDirty: false
                });
            }
            // Set invoice language from customer's invoice_language
            if (preselectedCustomer.invoice_language) {
                const languageCode = normalizeLanguageCode(preselectedCustomer.invoice_language);
                setValue("invoiceLanguage", languageCode, {
                    shouldDirty: false
                });
                setInvoiceLanguage(languageCode);
                if (!isHydratingRef.current) {
                    setDefaultTexts(languageCode);
                }
            }
        }, 300);
        return ()=>clearTimeout(timeoutId);
    }, [
        preselectedCustomerId,
        customers,
        editData,
        setValue,
        setInvoiceLanguage,
        watchFields.customerId
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsxs("form", {
                id: "invoice-form",
                onSubmit: handleSubmit(onSubmit, onError),
                onChangeCapture: markUserChanged,
                onInputCapture: markUserChanged,
                style: {
                    height: "calc(100% - 42px)"
                },
                children: [
                    /*#__PURE__*/ _jsx(CollapsibleSection, {
                        title: t("information"),
                        children: /*#__PURE__*/ _jsxs(Box, {
                            display: "flex",
                            flexDirection: "column",
                            gap: "20px",
                            children: [
                                /*#__PURE__*/ _jsx(Grid, {
                                    container: true,
                                    item: true,
                                    xs: 12,
                                    children: /*#__PURE__*/ _jsx(FormField, {
                                        title: t("selectCustomer"),
                                        type: "text",
                                        placeholder: "",
                                        name: "customerId",
                                        error: errors.customerId,
                                        searchSelect: true,
                                        options: customerList,
                                        control: control,
                                        additionalOptions: [
                                            {
                                                label: t("createNewCustomer"),
                                                redirect: "/".concat(lng, "/application/customers?create=1")
                                            }
                                        ],
                                        onSelect: (value)=>{
                                            if (isHydratingRef.current) return;
                                            markUserChanged();
                                            if (value) {
                                                setValue("paymentTerms", value.payment_terms);
                                                // Set invoice language from customer's invoice_language
                                                if (value.invoice_language) {
                                                    const languageCode = normalizeLanguageCode(value.invoice_language);
                                                    setValue("invoiceLanguage", languageCode);
                                                    setInvoiceLanguage(languageCode);
                                                    setDefaultTexts(languageCode);
                                                }
                                            }
                                        }
                                    })
                                }),
                                /*#__PURE__*/ _jsxs(Grid, {
                                    container: true,
                                    width: "100%",
                                    rowSpacing: "20",
                                    columnSpacing: {
                                        sm: "20"
                                    },
                                    children: [
                                        /*#__PURE__*/ _jsx(Grid, {
                                            item: true,
                                            xs: 12,
                                            sm: displayFields.refNo ? 4 : 6,
                                            children: /*#__PURE__*/ _jsx(FormField, {
                                                title: t("documentType"),
                                                type: "select",
                                                placeholder: "",
                                                name: "documentType",
                                                select: true,
                                                options: [
                                                    {
                                                        label: t("invoice"),
                                                        value: "invoice"
                                                    },
                                                    {
                                                        label: t("creditNote"),
                                                        value: "credit_note"
                                                    }
                                                ],
                                                control: control,
                                                disabled: true
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx(Grid, {
                                            item: true,
                                            xs: 12,
                                            sm: displayFields.refNo ? 4 : 6,
                                            children: /*#__PURE__*/ _jsx(FormField, {
                                                title: t("invoiceDate"),
                                                type: "text",
                                                placeholder: "",
                                                name: "invoiceDate",
                                                date: true,
                                                control: control,
                                                onSelect: ()=>{
                                                    if (isHydratingRef.current) return;
                                                    markUserChanged();
                                                }
                                            })
                                        }),
                                        displayFields.refNo && /*#__PURE__*/ _jsx(Grid, {
                                            item: true,
                                            xs: 12,
                                            sm: 4,
                                            children: /*#__PURE__*/ _jsx(FormField, {
                                                title: t("referenceNumber"),
                                                type: "text",
                                                placeholder: "",
                                                name: "referenceNumber",
                                                error: errors.referenceNumber,
                                                control: control
                                            })
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs(Grid, {
                                    container: true,
                                    width: "100%",
                                    rowSpacing: "20",
                                    columnSpacing: {
                                        sm: "20"
                                    },
                                    children: [
                                        /*#__PURE__*/ _jsx(Grid, {
                                            item: true,
                                            xs: 12,
                                            sm: 6,
                                            children: /*#__PURE__*/ _jsx(FormField, {
                                                title: t("paymentTerms"),
                                                type: "select",
                                                placeholder: "",
                                                name: "paymentTerms",
                                                error: errors.paymentTerms,
                                                select: true,
                                                options: paymentTerms(invoiceLanguage),
                                                control: control,
                                                onSelect: ()=>{
                                                    if (isHydratingRef.current) return;
                                                    markUserChanged();
                                                }
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx(Grid, {
                                            item: true,
                                            xs: 12,
                                            sm: 6,
                                            children: /*#__PURE__*/ _jsx(FormField, {
                                                title: t("selectLanguage"),
                                                type: "select",
                                                placeholder: "",
                                                name: "invoiceLanguage",
                                                select: true,
                                                options: [
                                                    {
                                                        label: t("english"),
                                                        value: "en"
                                                    },
                                                    {
                                                        label: t("german"),
                                                        value: "de"
                                                    }
                                                ],
                                                error: errors.product_name,
                                                control: control,
                                                onSelect: (val)=>{
                                                    if (isHydratingRef.current) return;
                                                    markUserChanged();
                                                    setInvoiceLanguage(val);
                                                    setDefaultTexts(val);
                                                }
                                            }, "invoice-language-".concat(lng))
                                        })
                                    ]
                                }),
                                (displayFields.serviceDate || displayFields.location) && /*#__PURE__*/ _jsxs(Grid, {
                                    container: true,
                                    width: "100%",
                                    rowSpacing: "20",
                                    columnSpacing: {
                                        sm: "20"
                                    },
                                    children: [
                                        displayFields.serviceDate && /*#__PURE__*/ _jsx(Grid, {
                                            item: true,
                                            xs: 12,
                                            sm: 6,
                                            children: /*#__PURE__*/ _jsxs(Box, {
                                                children: [
                                                    /*#__PURE__*/ _jsx(Typography, {
                                                        variant: "h6",
                                                        mb: "15px",
                                                        color: "#515151",
                                                        children: t("serviceDate")
                                                    }),
                                                    /*#__PURE__*/ _jsxs(Box, {
                                                        width: "100%",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "10px",
                                                        children: [
                                                            /*#__PURE__*/ _jsx(FormField, {
                                                                placeholder: "",
                                                                name: "serviceStartDate",
                                                                error: errors.serviceStartDate,
                                                                date: true,
                                                                control: control,
                                                                onSelect: (val)=>{
                                                                    if (isHydratingRef.current) return;
                                                                    markUserChanged();
                                                                    // If start date is ahead set end the same as start date
                                                                    if (dayjs(val).diff(dayjs(watchFields.serviceEndDate), "day") > 0) setValue("serviceEndDate", val);
                                                                }
                                                            }),
                                                            /*#__PURE__*/ _jsx("span", {
                                                                children: "-"
                                                            }),
                                                            /*#__PURE__*/ _jsx(FormField, {
                                                                placeholder: "",
                                                                name: "serviceEndDate",
                                                                error: errors.serviceEndDate,
                                                                date: true,
                                                                control: control,
                                                                onSelect: ()=>{
                                                                    if (isHydratingRef.current) return;
                                                                    markUserChanged();
                                                                }
                                                            })
                                                        ]
                                                    })
                                                ]
                                            })
                                        }),
                                        displayFields.location && /*#__PURE__*/ _jsx(Grid, {
                                            item: true,
                                            xs: 12,
                                            sm: 6,
                                            children: /*#__PURE__*/ _jsx(FormField, {
                                                title: t("servicePlace"),
                                                type: "text",
                                                placeholder: "",
                                                name: "servicePlace",
                                                error: errors.servicePlace,
                                                control: control
                                            })
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(Grid, {
                                    container: true,
                                    item: true,
                                    xs: 12,
                                    children: renderToggleField({
                                        keyName: "invoiceMessage",
                                        fieldName: "invoiceMessage",
                                        title: t("invoiceMessage"),
                                        rows: 4,
                                        error: errors.product_type,
                                        style: {
                                            marginBottom: "16px"
                                        }
                                    })
                                })
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsx(CollapsibleSection, {
                        title: t("tableBody"),
                        style: {
                            marginTop: "40px"
                        },
                        children: /*#__PURE__*/ _jsxs(Box, {
                            display: "flex",
                            flexDirection: "column",
                            gap: "20px",
                            children: [
                                fields.map((item, index)=>{
                                    var _errors_items_index;
                                    return /*#__PURE__*/ _jsx(CollapsibleSection, {
                                        iconSize: "18px",
                                        renderTitle: ()=>/*#__PURE__*/ _jsxs(Typography, {
                                                fontWeight: "400",
                                                variant: "h6",
                                                fontSize: "14px",
                                                lineHeight: "14px",
                                                children: [
                                                    /*#__PURE__*/ _jsx("span", {
                                                        style: {
                                                            fontWeight: 700,
                                                            paddingRight: "8px"
                                                        },
                                                        children: "#".concat(index + 1)
                                                    }),
                                                    /*#__PURE__*/ _jsx("span", {
                                                        style: {
                                                            color: "#515151"
                                                        },
                                                        children: t("product")
                                                    })
                                                ]
                                            }),
                                        headerActions: fields.length > 1 ? /*#__PURE__*/ _jsxs(_Fragment, {
                                            children: [
                                                /*#__PURE__*/ _jsx(IconButton, {
                                                    size: "small",
                                                    "aria-label": t("moveUp"),
                                                    disabled: index === 0,
                                                    onClick: ()=>handleItemMoveUp(index),
                                                    sx: {
                                                        width: "34px",
                                                        height: "34px"
                                                    },
                                                    children: /*#__PURE__*/ _jsx(KeyboardArrowUpIcon, {
                                                        sx: {
                                                            fontSize: "20px"
                                                        }
                                                    })
                                                }),
                                                /*#__PURE__*/ _jsx(IconButton, {
                                                    size: "small",
                                                    "aria-label": t("moveDown"),
                                                    disabled: index === fields.length - 1,
                                                    onClick: ()=>handleItemMoveDown(index),
                                                    sx: {
                                                        width: "34px",
                                                        height: "34px"
                                                    },
                                                    children: /*#__PURE__*/ _jsx(KeyboardArrowDownIcon, {
                                                        sx: {
                                                            fontSize: "20px"
                                                        }
                                                    })
                                                })
                                            ]
                                        }) : null,
                                        secondaryAction: ()=>handleItemDelete(index, item),
                                        secondaryActionIcon: /*#__PURE__*/ _jsx(DeleteIcon2, {}),
                                        children: /*#__PURE__*/ _jsxs(Box, {
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: "20px",
                                            children: [
                                                /*#__PURE__*/ _jsx(Grid, {
                                                    container: true,
                                                    item: true,
                                                    xs: 12,
                                                    children: /*#__PURE__*/ _jsx(FormField, {
                                                        title: t("selectProduct"),
                                                        type: "text",
                                                        placeholder: "",
                                                        name: "items[".concat(index, "].productId"),
                                                        error: errors.items && errors.items[index] && ((_errors_items_index = errors.items[index]) === null || _errors_items_index === void 0 ? void 0 : _errors_items_index.productId) ? {
                                                            message: t("selectProductToProceed")
                                                        } : null,
                                                        searchSelect: true,
                                                        options: [
                                                            ...productList,
                                                            {
                                                                label: t("createNewProduct"),
                                                                value: "new-product",
                                                                isCreateNew: true
                                                            }
                                                        ],
                                                        control: control,
                                                        freeSolo: true,
                                                        renderOption: (props, option)=>{
                                                            const isCreateNewOption = Boolean(option === null || option === void 0 ? void 0 : option.isCreateNew);
                                                            const name = (option === null || option === void 0 ? void 0 : option.product_name) || (option === null || option === void 0 ? void 0 : option.label) || (option === null || option === void 0 ? void 0 : option.productName) || "";
                                                            const desc = (option === null || option === void 0 ? void 0 : option.description) || "";
                                                            return /*#__PURE__*/ _createElement("li", {
                                                                ...props,
                                                                key: option.value || option.redirect || option.label || name,
                                                                style: {
                                                                    ...props.style,
                                                                    ...isCreateNewOption ? {
                                                                        borderTop: "1px solid #E0E0E0",
                                                                        color: "#515151",
                                                                        fontSize: "14px",
                                                                        fontWeight: 500,
                                                                        marginTop: "4px"
                                                                    } : {}
                                                                }
                                                            }, /*#__PURE__*/ _jsxs(Box, {
                                                                display: "flex",
                                                                flexDirection: isCreateNewOption ? "row" : "column",
                                                                alignItems: isCreateNewOption ? "center" : "flex-start",
                                                                gap: isCreateNewOption ? "8px" : 0,
                                                                children: [
                                                                    isCreateNewOption ? /*#__PURE__*/ _jsx(Box, {
                                                                        component: "span",
                                                                        display: "inline-flex",
                                                                        alignItems: "center",
                                                                        justifyContent: "center",
                                                                        width: "20px",
                                                                        height: "20px",
                                                                        borderRadius: "50%",
                                                                        bgcolor: "#FFF4E6",
                                                                        color: "#FF9F24",
                                                                        fontSize: "15px",
                                                                        fontWeight: 600,
                                                                        children: "+"
                                                                    }) : null,
                                                                    /*#__PURE__*/ _jsx(Typography, {
                                                                        variant: "body2",
                                                                        fontWeight: 500,
                                                                        children: name
                                                                    }),
                                                                    !isCreateNewOption && desc ? /*#__PURE__*/ _jsx(Typography, {
                                                                        variant: "caption",
                                                                        color: "text.secondary",
                                                                        children: desc
                                                                    }) : null
                                                                ]
                                                            }));
                                                        },
                                                        onSelect: (value)=>{
                                                            if (isHydratingRef.current) return;
                                                            // Autocomplete changes don't bubble to form onChangeCapture.
                                                            markUserChanged();
                                                            // Handle clear/deselect (value can be null)
                                                            if (!value) {
                                                                setValue("items[".concat(index, "].unit"), "", {
                                                                    shouldDirty: true
                                                                });
                                                                setValue("items[".concat(index, "].vat"), "0", {
                                                                    shouldDirty: true
                                                                });
                                                                setValue("items[".concat(index, "].vatType"), "", {
                                                                    shouldDirty: true
                                                                });
                                                                setValue("items[".concat(index, "].unitPrice"), "", {
                                                                    shouldDirty: true
                                                                });
                                                                setValue("items[".concat(index, "].productId"), null, {
                                                                    shouldDirty: true
                                                                });
                                                                setValue("items[".concat(index, "].productName"), "", {
                                                                    shouldDirty: true
                                                                });
                                                                setValue("items[".concat(index, "].productType"), "", {
                                                                    shouldDirty: true
                                                                });
                                                                setValue("items[".concat(index, "].lineType"), "catalog", {
                                                                    shouldDirty: true
                                                                });
                                                                setValue("items[".concat(index, "].description"), "", {
                                                                    shouldDirty: true
                                                                });
                                                                return;
                                                            }
                                                            // Creating a new product
                                                            if (typeof value === "object" && value.isCreateNew) {
                                                                window.location.href = "/".concat(lng, "/application/products/new");
                                                                return;
                                                            }
                                                            // Handle custom typed value from freeSolo (object with label and no product_id)
                                                            if (typeof value === "object" && value.label && !value.product_id && !value.unit) {
                                                                // This is a custom typed product
                                                                setValue("items[".concat(index, "].productName"), value.label, {
                                                                    shouldDirty: true
                                                                });
                                                                setValue("items[".concat(index, "].productType"), "one-time", {
                                                                    shouldDirty: true
                                                                });
                                                                setValue("items[".concat(index, "].lineType"), "one_time", {
                                                                    shouldDirty: true
                                                                });
                                                                setValue("items[".concat(index, "].vatType"), null, {
                                                                    shouldDirty: true
                                                                });
                                                                // Set VAT to 0% if company has VAT exemption
                                                                if (hasVatExemption) {
                                                                    setValue("items[".concat(index, "].vat"), "0", {
                                                                        shouldDirty: true
                                                                    });
                                                                }
                                                                return;
                                                            }
                                                            if (typeof value === "object") {
                                                                // If a product from the list is selected
                                                                setValue("items[".concat(index, "].unit"), value.unit, {
                                                                    shouldValidate: true,
                                                                    shouldDirty: true
                                                                });
                                                                const taxRate = value.tax_rate;
                                                                const productVatType = value.vat_type;
                                                                const taxRateValue = resolveProductTaxRateValue(taxRate, productVatType, hasVatExemption);
                                                                setValue("items[".concat(index, "].vat"), taxRateValue, {
                                                                    shouldValidate: true,
                                                                    shouldDirty: true
                                                                });
                                                                // Trigger validation to ensure the Controller updates
                                                                trigger("items[".concat(index, "].vat"));
                                                                const vatType = hasVatExemption ? null : productVatType || null;
                                                                setValue("items[".concat(index, "].vatType"), vatType, {
                                                                    shouldValidate: true,
                                                                    shouldDirty: true
                                                                });
                                                                setValue("items[".concat(index, "].unitPrice"), formatStringToEuropeanCurrency(value.netto_price), {
                                                                    shouldDirty: true
                                                                });
                                                                // Do NOT overwrite productId with a plain string id.
                                                                // `FormField`'s Controller will set the full object into RHF state.
                                                                setValue("items[".concat(index, "].productName"), value.product_name, {
                                                                    shouldDirty: true
                                                                });
                                                                setValue("items[".concat(index, "].productType"), value.product_type || "", {
                                                                    shouldDirty: true
                                                                });
                                                                setValue("items[".concat(index, "].lineType"), "catalog", {
                                                                    shouldDirty: true
                                                                });
                                                                // Populate description if product has one
                                                                if (value.description) {
                                                                    setValue("items[".concat(index, "].description"), value.description, {
                                                                        shouldValidate: true,
                                                                        shouldDirty: true
                                                                    });
                                                                }
                                                                // Populate message fields if product has special VAT types (13b or 25a)
                                                                if ((invoiceSettings === null || invoiceSettings === void 0 ? void 0 : invoiceSettings.invoiceDetailRecord) && invoiceLanguage && (vatType === "13b" || vatType === "25a")) {
                                                                    const invoice = invoiceSettings.invoiceDetailRecord;
                                                                    const values = {
                                                                        en: {},
                                                                        de: {}
                                                                    };
                                                                    Object.entries(invoice).forEach((item)=>{
                                                                        if (item[0].includes("_en")) {
                                                                            const key = item[0].replace("_en", "");
                                                                            values.en[key] = item[1];
                                                                        } else if (item[0].includes("_de")) {
                                                                            const key = item[0].replace("_de", "");
                                                                            values.de[key] = item[1];
                                                                        }
                                                                    });
                                                                    if (vatType === "13b" && messageEnabled.message13b && (!watchFields.message13b || watchFields.message13b.trim() === "")) {
                                                                        var _values_invoiceLanguage;
                                                                        setValue("message13b", ((_values_invoiceLanguage = values[invoiceLanguage]) === null || _values_invoiceLanguage === void 0 ? void 0 : _values_invoiceLanguage.message_13b) || "");
                                                                    }
                                                                    if (vatType === "25a" && messageEnabled.message25a && (!watchFields.message25a || watchFields.message25a.trim() === "")) {
                                                                        var _values_invoiceLanguage1;
                                                                        setValue("message25a", ((_values_invoiceLanguage1 = values[invoiceLanguage]) === null || _values_invoiceLanguage1 === void 0 ? void 0 : _values_invoiceLanguage1.message_25a) || "");
                                                                    }
                                                                }
                                                                return;
                                                            }
                                                            if (typeof value === "string") {
                                                                // If a custom product is entered, store it in productName
                                                                setValue("items[".concat(index, "].productName"), value, {
                                                                    shouldDirty: true
                                                                });
                                                                setValue("items[".concat(index, "].productType"), "one-time", {
                                                                    shouldDirty: true
                                                                });
                                                                setValue("items[".concat(index, "].lineType"), "one_time", {
                                                                    shouldDirty: true
                                                                });
                                                                setValue("items[".concat(index, "].vatType"), null, {
                                                                    shouldDirty: true
                                                                });
                                                                // Set VAT to 0% if company has VAT exemption
                                                                if (hasVatExemption) {
                                                                    setValue("items[".concat(index, "].vat"), "0", {
                                                                        shouldDirty: true
                                                                    });
                                                                }
                                                            }
                                                        },
                                                        onInputChange: (event, newInputValue, reason)=>{
                                                            if (!isHydratingRef.current && reason !== "reset") {
                                                                markUserChanged();
                                                            }
                                                            // Store typed value - update productName for PDF rendering
                                                            // Use shouldValidate: true and shouldDirty: true to ensure it triggers watch
                                                            setValue("items[".concat(index, "].productName"), newInputValue, {
                                                                shouldValidate: true,
                                                                shouldDirty: true
                                                            });
                                                            setValue("items[".concat(index, "].productType"), "one-time", {
                                                                shouldValidate: false,
                                                                shouldDirty: false
                                                            });
                                                            setValue("items[".concat(index, "].lineType"), "one_time", {
                                                                shouldValidate: false,
                                                                shouldDirty: false
                                                            });
                                                            // Set VAT to 0% if company has VAT exemption
                                                            if (hasVatExemption) {
                                                                setValue("items[".concat(index, "].vat"), "0", {
                                                                    shouldValidate: false,
                                                                    shouldDirty: false
                                                                });
                                                            }
                                                        },
                                                        renderInput: (params)=>{
                                                            var _errors_items_index;
                                                            return /*#__PURE__*/ _jsx(TextField, {
                                                                ...params,
                                                                label: t("selectProduct"),
                                                                variant: "outlined",
                                                                error: Boolean(errors.items && errors.items[index] && ((_errors_items_index = errors.items[index]) === null || _errors_items_index === void 0 ? void 0 : _errors_items_index.productId))
                                                            });
                                                        }
                                                    })
                                                }),
                                                /*#__PURE__*/ _jsx(Grid, {
                                                    container: true,
                                                    item: true,
                                                    xs: 12,
                                                    children: /*#__PURE__*/ _jsx(FormField, {
                                                        title: t("description"),
                                                        type: "text",
                                                        placeholder: "",
                                                        name: "items[".concat(index, "].description"),
                                                        multiline: true,
                                                        rows: 3,
                                                        error: errors.items && errors.items[index] && errors.items[index].description,
                                                        options: productList,
                                                        control: control
                                                    })
                                                }),
                                                /*#__PURE__*/ _jsxs(Grid, {
                                                    container: true,
                                                    width: "100%",
                                                    rowSpacing: "20",
                                                    columnSpacing: {
                                                        sm: "20"
                                                    },
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Grid, {
                                                            item: true,
                                                            xs: 12,
                                                            sm: 3,
                                                            children: /*#__PURE__*/ _jsx(FormField, {
                                                                title: t("quantity"),
                                                                type: "number",
                                                                placeholder: "",
                                                                name: "items[".concat(index, "].quantity"),
                                                                error: errors.items && errors.items[index] && errors.items[index].quantity,
                                                                control: control,
                                                                inputProps: {
                                                                    min: "0.01",
                                                                    step: "0.01"
                                                                },
                                                                onKeyDown: (e)=>{
                                                                    // Prevent negative sign
                                                                    if (e.key === "-") {
                                                                        e.preventDefault();
                                                                    }
                                                                }
                                                            })
                                                        }),
                                                        /*#__PURE__*/ _jsx(Grid, {
                                                            item: true,
                                                            xs: 12,
                                                            sm: 3,
                                                            children: /*#__PURE__*/ _jsx(FormField, {
                                                                title: t("unit"),
                                                                type: "text",
                                                                placeholder: "",
                                                                name: "items[".concat(index, "].unit"),
                                                                select: true,
                                                                options: getUnitOptions(tProducts),
                                                                error: errors.items && errors.items[index] && errors.items[index].unit,
                                                                control: control
                                                            })
                                                        }),
                                                        /*#__PURE__*/ _jsx(Grid, {
                                                            item: true,
                                                            xs: 12,
                                                            sm: 3,
                                                            children: /*#__PURE__*/ _jsx(FormField, {
                                                                title: t("tax"),
                                                                type: "text",
                                                                placeholder: "",
                                                                name: "items[".concat(index, "].vat"),
                                                                select: true,
                                                                options: taxRateOptions,
                                                                error: errors.items && errors.items[index] && errors.items[index].vat,
                                                                control: control,
                                                                disabled: hasVatExemption,
                                                                onSelect: (newTaxRate)=>{
                                                                    if (isHydratingRef.current) return;
                                                                    markUserChanged();
                                                                    // Parse the tax rate to extract vatType (e.g., '0_13b' -> vatType: '13b')
                                                                    const parts = newTaxRate.split("_");
                                                                    const taxRateValue = parts[0];
                                                                    const vatType = parts[1] || undefined;
                                                                    // Update the vatType field
                                                                    setValue("items[".concat(index, "].vatType"), vatType, {
                                                                        shouldValidate: true,
                                                                        shouldDirty: true
                                                                    });
                                                                    // Check if any items still have 13b or 25a after this change
                                                                    const updatedItems = [
                                                                        ...watchFields.items
                                                                    ];
                                                                    updatedItems[index] = {
                                                                        ...updatedItems[index],
                                                                        vat: newTaxRate,
                                                                        vatType: vatType || undefined
                                                                    };
                                                                    const has13b = updatedItems.some((item)=>{
                                                                        if (!item.vat) return false;
                                                                        const vatStr = item.vat.toString();
                                                                        const itemVatType = item.vatType;
                                                                        return itemVatType === "13b" || vatStr.includes("_13b") || vatStr === "0_13b";
                                                                    });
                                                                    const has25a = updatedItems.some((item)=>{
                                                                        if (!item.vat) return false;
                                                                        const vatStr = item.vat.toString();
                                                                        const itemVatType = item.vatType;
                                                                        return itemVatType === "25a" || vatStr.includes("_25a") || vatStr === "0_25a";
                                                                    });
                                                                    // Populate or clear message fields based on whether items have the corresponding VAT type
                                                                    if (has13b) {
                                                                        // Set default text from invoice settings if field is empty or undefined
                                                                        const currentValue = watchFields.message13b;
                                                                        if (messageEnabled.message13b && (!currentValue || currentValue.trim() === "")) {
                                                                            if ((invoiceSettings === null || invoiceSettings === void 0 ? void 0 : invoiceSettings.invoiceDetailRecord) && invoiceLanguage) {
                                                                                var _values_invoiceLanguage;
                                                                                const invoice = invoiceSettings.invoiceDetailRecord;
                                                                                const values = {
                                                                                    en: {},
                                                                                    de: {}
                                                                                };
                                                                                Object.entries(invoice).forEach((item)=>{
                                                                                    if (item[0].includes("_en")) {
                                                                                        const key = item[0].replace("_en", "");
                                                                                        values.en[key] = item[1];
                                                                                    } else if (item[0].includes("_de")) {
                                                                                        const key = item[0].replace("_de", "");
                                                                                        values.de[key] = item[1];
                                                                                    }
                                                                                });
                                                                                setValue("message13b", ((_values_invoiceLanguage = values[invoiceLanguage]) === null || _values_invoiceLanguage === void 0 ? void 0 : _values_invoiceLanguage.message_13b) || "", {
                                                                                    shouldValidate: true,
                                                                                    shouldDirty: true
                                                                                });
                                                                            }
                                                                        }
                                                                    } else {
                                                                        setValue("message13b", "", {
                                                                            shouldValidate: true,
                                                                            shouldDirty: true
                                                                        });
                                                                    }
                                                                    if (has25a) {
                                                                        // Set default text from invoice settings if field is empty or undefined
                                                                        const currentValue = watchFields.message25a;
                                                                        if (messageEnabled.message25a && (!currentValue || currentValue.trim() === "")) {
                                                                            if ((invoiceSettings === null || invoiceSettings === void 0 ? void 0 : invoiceSettings.invoiceDetailRecord) && invoiceLanguage) {
                                                                                var _values_invoiceLanguage1;
                                                                                const invoice = invoiceSettings.invoiceDetailRecord;
                                                                                const values = {
                                                                                    en: {},
                                                                                    de: {}
                                                                                };
                                                                                Object.entries(invoice).forEach((item)=>{
                                                                                    if (item[0].includes("_en")) {
                                                                                        const key = item[0].replace("_en", "");
                                                                                        values.en[key] = item[1];
                                                                                    } else if (item[0].includes("_de")) {
                                                                                        const key = item[0].replace("_de", "");
                                                                                        values.de[key] = item[1];
                                                                                    }
                                                                                });
                                                                                setValue("message25a", ((_values_invoiceLanguage1 = values[invoiceLanguage]) === null || _values_invoiceLanguage1 === void 0 ? void 0 : _values_invoiceLanguage1.message_25a) || "", {
                                                                                    shouldValidate: true,
                                                                                    shouldDirty: true
                                                                                });
                                                                            }
                                                                        }
                                                                    } else {
                                                                        setValue("message25a", "", {
                                                                            shouldValidate: true,
                                                                            shouldDirty: true
                                                                        });
                                                                    }
                                                                }
                                                            })
                                                        }),
                                                        /*#__PURE__*/ _jsx(Grid, {
                                                            item: true,
                                                            xs: 12,
                                                            sm: 3,
                                                            children: /*#__PURE__*/ _jsx(FormField, {
                                                                title: t("netPrice"),
                                                                type: "text",
                                                                placeholder: "",
                                                                name: "items[".concat(index, "].unitPrice"),
                                                                transform: formatStringToEuropeanCurrency,
                                                                error: errors.items && errors.items[index] && errors.items[index].unitPrice,
                                                                control: control
                                                            })
                                                        })
                                                    ]
                                                })
                                            ]
                                        })
                                    }, item.id);
                                }),
                                /*#__PURE__*/ _jsx(ReusableButton, {
                                    name: t("addProduct"),
                                    backgroundColor: "#fff",
                                    color: colors.button.secondary,
                                    borderColor: colors.button.secondary,
                                    onClick: handleItemAppend
                                })
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsx(CollapsibleSection, {
                        title: t("text"),
                        style: {
                            marginTop: "40px"
                        },
                        children: /*#__PURE__*/ _jsxs(Box, {
                            display: "flex",
                            flexDirection: "column",
                            gap: "20px",
                            children: [
                                shouldShowMessage19() && /*#__PURE__*/ _jsx(Grid, {
                                    container: true,
                                    item: true,
                                    xs: 12,
                                    children: renderToggleField({
                                        keyName: "message19",
                                        fieldName: "message19",
                                        title: t("message19"),
                                        rows: 2,
                                        error: errors.message19
                                    })
                                }),
                                shouldShowMessage4() && /*#__PURE__*/ _jsx(Grid, {
                                    container: true,
                                    item: true,
                                    xs: 12,
                                    children: renderToggleField({
                                        keyName: "message4",
                                        fieldName: "message4",
                                        title: t("message4"),
                                        rows: 2,
                                        error: errors.message4
                                    })
                                }),
                                shouldShowMessage13b() && /*#__PURE__*/ _jsx(Grid, {
                                    container: true,
                                    item: true,
                                    xs: 12,
                                    children: renderToggleField({
                                        keyName: "message13b",
                                        fieldName: "message13b",
                                        title: t("message13b"),
                                        rows: 2,
                                        error: errors.message13b
                                    })
                                }),
                                shouldShowMessage25a() && /*#__PURE__*/ _jsx(Grid, {
                                    container: true,
                                    item: true,
                                    xs: 12,
                                    children: renderToggleField({
                                        keyName: "message25a",
                                        fieldName: "message25a",
                                        title: t("message25a"),
                                        rows: 2,
                                        error: errors.message25a
                                    })
                                }),
                                /*#__PURE__*/ _jsx(Grid, {
                                    container: true,
                                    item: true,
                                    xs: 12,
                                    children: renderToggleField({
                                        keyName: "paymentMethod",
                                        fieldName: "paymentMethod",
                                        title: t("paymentMethods"),
                                        rows: 2,
                                        error: errors.product_type
                                    })
                                }),
                                discountSectionEnabled && /*#__PURE__*/ _jsx(Grid, {
                                    container: true,
                                    item: true,
                                    xs: 12,
                                    children: renderToggleField({
                                        keyName: "discount",
                                        fieldName: "discount",
                                        title: t("discount"),
                                        rows: 2,
                                        error: errors.product_type
                                    })
                                }),
                                /*#__PURE__*/ _jsx(Grid, {
                                    container: true,
                                    item: true,
                                    xs: 12,
                                    children: renderToggleField({
                                        keyName: "additionalText1",
                                        fieldName: "additionalText1",
                                        title: "".concat(t("additionalText"), " 1"),
                                        rows: 2,
                                        error: errors.product_type
                                    })
                                }),
                                /*#__PURE__*/ _jsx(Grid, {
                                    container: true,
                                    item: true,
                                    xs: 12,
                                    children: renderToggleField({
                                        keyName: "additionalText2",
                                        fieldName: "additionalText2",
                                        title: "".concat(t("additionalText"), " 2"),
                                        rows: 2,
                                        error: errors.product_type,
                                        showDisabledPreview: true
                                    })
                                })
                            ]
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(ConfirmationModal, {
                title: t("exclusiveBlockTitle"),
                subtitle: t("exclusiveBlockMessage"),
                open: exclusiveBlockModal.open,
                handleClose: ()=>setExclusiveBlockModal({
                        open: false,
                        targetKey: null,
                        targetField: null
                    }),
                primaryBtnName: t("exclusiveBlockConfirm"),
                primaryBtnAction: handleExclusiveBlockConfirm,
                secondaryBtnName: t("cancel"),
                secondaryBtnAction: ()=>setExclusiveBlockModal({
                        open: false,
                        targetKey: null,
                        targetField: null
                    })
            }),
            /*#__PURE__*/ _jsx(ConfirmationModal, {
                title: t("saveFailed"),
                subtitle: errorMessage || t("saveFailedMessage"),
                open: showError,
                handleClose: ()=>{
                    setShowError(false);
                    setErrorMessage("");
                },
                primaryBtnName: t("ok"),
                primaryBtnAction: ()=>{
                    setShowError(false);
                    setErrorMessage("");
                }
            }),
            /*#__PURE__*/ _jsx(SnackBar, {
                open: showSuccess,
                type: "success",
                handleClose: ()=>setShowSuccess(false),
                children: t("saveSuccess")
            })
        ]
    });
};
export default InvoiceForm;
